import React, { useState } from 'react';
import { CountdownTimer } from '../components/CountdownTimer';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { useGeoPrice } from '../hooks/useGeoPrice';
import { trackPixelEvent } from '../utils/metaPixel';
import { CheckCircle2, ChevronRight, Clock, ShieldCheck, Download, Smartphone, Check, PlaneTakeoff, Brain, HeartHandshake, Headphones, BookOpen, FileText, ListChecks } from 'lucide-react';
const IMAGES = {
  logo: 'https://cdn.magicpatterns.com/uploads/gunxqmX3DLE3VayEChcB9z/LOGO_ATLAS.png',
  aerofobia: 'https://cdn.magicpatterns.com/uploads/1mVdpFchd5giBM25U8BsiK/AEROFOBIA.png',
  checklist: 'https://cdn.magicpatterns.com/uploads/wDAogEQctEEugt1Ckfp8e1/CHECKLIST.png',
  cuaderno: 'https://cdn.magicpatterns.com/uploads/8TaniZ9QhgVHdEYDseeY6z/Cuaderno_de_ejercicios.png',
  guiaSos: 'https://cdn.magicpatterns.com/uploads/eGWxKbZFeAThtG1LpDP8Pk/GUIA_SOS.png',
  biblioteca: 'https://cdn.magicpatterns.com/uploads/7NE7uCYZUcEmekCjGdKWyc/BIBLIOTECA_DE_CALMA_EN_VUELO.png'
};
const FAQS = [{
  q: '¿Esto elimina el miedo a volar?',
  a: 'No prometemos eliminar el miedo de un día para el otro. Te da herramientas prácticas para entenderlo, regular la ansiedad y atravesar el vuelo con más calma.'
}, {
  q: '¿Cuándo recibo el acceso?',
  a: 'El acceso es inmediato. Podés empezar a leer, escuchar y completar los ejercicios apenas recibís el material.'
}, {
  q: '¿Puedo usarlo durante el vuelo?',
  a: 'Sí. La Guía SOS está pensada para leer en el avión y los audios pueden escucharse con auriculares sin conexión (si los descargás antes).'
}, {
  q: '¿Los audios necesitan internet?',
  a: 'Recomendamos descargarlos antes del viaje para poder escucharlos incluso si no tenés conexión durante el vuelo.'
}, {
  q: '¿Es un tratamiento psicológico?',
  a: 'No. Es un recurso educativo y de acompañamiento. No reemplaza atención médica ni tratamiento psicológico profesional.'
}];
const CART_URL = 'https://atlas-de-viajes.impultienda.ar/checkout/aerofobia-guia-para-volar-en-calma';
export function Landing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const {
    price,
    isLoading: isPriceLoading
  } = useGeoPrice();
  const scrollToBuy = () => {
    document.getElementById('compra')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const buyNow = () => {
    trackPixelEvent('InitiateCheckout', {
      content_name: 'Aerofobia: Guía para Volar en Calma',
      currency: price.currency,
      value: price.amount
    });
    window.location.href = CART_URL;
  };
  return <div className="min-h-screen bg-polar font-sans text-oceano" data-id="element-31" data-mp="r4fw2xd1r4nw">
      <SocialProofPopup data-id="element-32" data-mp="v3opu28372jg" />

      {/* SECCIÓN 1 — Barra superior */}
      <div className="fixed top-0 left-0 right-0 z-50" data-id="element-33" data-mp="2lgwa4kc33n3b">
        <div className="bg-oceano text-polar text-[10px] md:text-sm py-1.5 md:py-2 text-center font-medium tracking-wide" data-id="element-34" data-mp="2lhe3n97d3n7j">
          Producto 100% digital · Acceso inmediato
        </div>
        <div className="bg-travertino text-oceano text-xs md:text-sm py-2 text-center font-medium flex items-center justify-center gap-2 shadow-sm" data-id="element-35" data-mp="dvxj61qifevi">
          <Clock size={16} className="text-oceano" data-id="element-36" data-mp="nkxjbogr0gba" />
          <span data-id="element-37" data-mp="nlpfb0e9cg23">Oferta por tiempo limitado · termina en:</span>
          <CountdownTimer initialMinutes={20} data-id="element-38" data-mp="iuh35wljmgl8" />
        </div>
      </div>

      {/* HERO MOBILE-FIRST: logo → título/subtítulo → imagen → texto */}
      <section className="pt-28 pb-12 md:pt-40 md:pb-20 px-4 max-w-3xl mx-auto flex flex-col items-center text-center space-y-6 md:space-y-8" data-id="element-39" data-mp="xhk3sc55x5cs">
        {/* 1. Logo */}
        <img src={IMAGES.logo} alt="Atlas de Viajes" className="h-24 md:h-32 object-contain" data-id="element-40" data-mp="16k1rkv0gu61w" />

        {/* 2. Título y subtítulo */}
        <div data-id="element-41" data-mp="11li2wmuhmg5u">
          <h1 className="font-cormorant text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight mb-4" data-id="element-42" data-mp="11im30sz2xher">
            No dejes que el <span className="text-caribe" data-id="element-43" data-mp="33cbmdl7ljhj4">miedo</span> decida tu
            próximo viaje
          </h1>
          <p className="text-lg md:text-2xl text-hierro font-medium leading-snug" data-id="element-44" data-mp="35r0a74yxgpyt">
            Un método práctico para calmar la ansiedad, entender lo que pasa en
            el avión y vivir tu próximo vuelo con más tranquilidad.
          </p>
        </div>

        {/* 3. Imagen */}
        <div className="w-full max-w-sm mx-auto relative mt-2" data-id="element-45" data-mp="3jqht53pjl909">
          <div className="absolute -top-4 -right-4 w-28 h-28 bg-travertino/20 rounded-full blur-2xl" data-id="element-46" data-mp="3jpqaeth1n5re"></div>
          <div className="absolute -bottom-4 -left-4 w-28 h-28 bg-caribe/20 rounded-full blur-2xl" data-id="element-47" data-mp="2m4kfbjm3xrun"></div>
          <img src={IMAGES.aerofobia} alt="Mockup Aerofobia" className="relative z-10 w-full h-auto rounded-2xl shadow-xl object-contain" data-id="element-48" data-mp="2qzrhuo3n4qhz" />
        </div>

        {/* 4. Texto y todo */}

        <ul className="space-y-3 w-full max-w-md text-left" data-id="element-49" data-mp="30syhrtfm3eao">
          {['Para ansiedad antes del vuelo.', 'Para miedo a turbulencias y ruidos.', 'Para usar en el aeropuerto y en el avión.', 'Incluye guía, audios, ejercicios y checklist.'].map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-50" data-mp="20sd8lyhllqze" data-mpi={i}>
              <CheckCircle2 className="text-caribe shrink-0 mt-0.5" size={20} data-id="element-51" data-mp="20pqzhuf273f2" />
              <span className="text-oceano font-medium" data-id="element-52" data-mp="1vta0u6a8h6nq">{bullet}</span>
            </li>)}
        </ul>

        <div className="w-full max-w-md pt-2" data-id="element-53" data-mp="1qs2tu9fx0bi4">
          <button onClick={scrollToBuy} className="w-full bg-caribe hover:bg-[#24a8a2] text-polar px-8 py-4 md:px-10 md:py-5 rounded-xl font-semibold text-lg md:text-xl transition-colors shadow-lg shadow-caribe/20 flex items-center justify-center gap-2" data-id="element-54" data-mp="1lut5kx49i5vv">
            Quiero volar con más calma
          </button>
          <p className="text-xs md:text-sm text-hierro flex items-center justify-center gap-2 mt-4" data-id="element-55" data-mp="182d07pnw1vrs">
            Acceso inmediato · Producto digital
          </p>
        </div>
      </section>

      {/* SECCIÓN DE DOLOR MÁS HUMANA */}
      <section className="bg-oceano text-polar py-16 md:py-20 px-4" data-id="element-56" data-mp="y4gs76q8zgxw">
        <div className="max-w-4xl mx-auto" data-id="element-57" data-mp="u34yoq496bkf">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold mb-6 text-center md:text-left" data-id="element-58" data-mp="u5hotqk5zs8k">
            ¿Sentís que el miedo empieza mucho antes del vuelo?
          </h2>
          

          <div className="grid sm:grid-cols-2 gap-4 mb-12" data-id="element-59" data-mp="of9sa4o4i6mu">
            {['Te cuesta dormir la noche anterior.', 'Pensás "¿y si me agarra ansiedad en el avión?"', 'Cada ruido te hace imaginar lo peor.', 'La turbulencia te activa el cuerpo.', 'Sentís que no tener control es lo más difícil.', 'Querés viajar, pero el miedo te frena.'].map((q, i) => <div key={i} className="bg-polar/5 p-5 rounded-xl border border-polar/10 flex items-start gap-3" data-id="element-60" data-mp="177m2fdl0qc7j" data-mpi={i}>
                <div className="w-1.5 h-1.5 rounded-full bg-travertino mt-2 shrink-0" data-id="element-61" data-mp="th8xzl7c5fmy"></div>
                <p className="text-polar/90 font-medium" data-id="element-62" data-mp="yf0m044v0hlv">{q}</p>
              </div>)}
          </div>

          <div className="bg-travertino/10 border border-travertino/20 p-6 rounded-2xl text-center mb-8" data-id="element-63" data-mp="1m4l7ousedsyu">
            <p className="font-cormorant text-2xl md:text-3xl text-travertino italic" data-id="element-64" data-mp="1qw389hqidrja">
              "El problema no es que no quieras viajar. Es que no querés volver
              a sufrir todo el vuelo."
            </p>
          </div>

          <p className="text-center text-polar/90 text-lg" data-id="element-65" data-mp="1w8t9vu1xk0qe">
            Por eso este método no te habla desde la teoría. Te da herramientas
            simples para saber qué hacer cuando el miedo aparece.
          </p>
        </div>
      </section>

      {/* SECCIÓN PUENTE / NUEVA CREENCIA */}
      <section className="py-16 md:py-20 px-4 bg-polar" data-id="element-66" data-mp="1hcatzvivwsky">
        <div className="max-w-5xl mx-auto text-center" data-id="element-67" data-mp="1dapfy6um6896">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano mb-6" data-id="element-68" data-mp="1dcaa3vd0np6i">
            No necesitás controlar el avión. Necesitás un plan para tu ansiedad.
          </h2>
          <p className="text-lg md:text-xl text-hierro mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-69" data-mp="1m9kjfb69x48s">
            Cuando entendés qué pasa en tu cuerpo, qué cosas son normales
            durante el vuelo y qué hacer en los momentos difíciles, el miedo
            deja de sentirse tan gigante.
          </p>

          <p className="font-semibold text-oceano text-xl mb-8" data-id="element-70" data-mp="vys8lfje5zjt">
            El Método Volar en Calma trabaja en 3 pasos:
          </p>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-71" data-mp="1511hk0z0hkv3">
            {[{
            title: 'Entender',
            desc: 'Qué es la aerofobia, por qué aparece la ansiedad y qué significan muchas sensaciones del vuelo.',
            icon: <Brain className="text-caribe" size={32} data-id="element-72" data-mp="1eqbbznhbvfmd" />
          }, {
            title: 'Preparar',
            desc: 'Ejercicios, checklist y recursos para llegar al aeropuerto con más claridad.',
            icon: <PlaneTakeoff className="text-caribe" size={32} data-id="element-73" data-mp="1ntcg5immuwne" />
          }, {
            title: 'Acompañar',
            desc: 'Guía SOS, audios y meditaciones para usar antes y durante el vuelo.',
            icon: <HeartHandshake className="text-caribe" size={32} data-id="element-74" data-mp="3pkpfzt9quecl" />
          }].map((pillar, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-travertino/20 text-center flex flex-col items-center" data-id="element-75" data-mp="3ujjzyyjg68ya" data-mpi={i}>
                <div className="w-16 h-16 bg-caribe/10 rounded-full flex items-center justify-center mb-4" data-id="element-76" data-mp="3n12myicd4c0k">
                  {pillar.icon}
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-3" data-id="element-77" data-mp="3d9y7zimosr5q">
                  {pillar.title}
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-78" data-mp="3888tft099vcw">
                  {pillar.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* QUÉ INCLUYE EL MÉTODO */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0]" data-id="element-79" data-mp="3vze9a8674nn7">
        <div className="max-w-6xl mx-auto" data-id="element-80" data-mp="3vxuuyn43omo2">
          <div className="text-center mb-12" data-id="element-81" data-mp="3rylyvbll8cpc">
            <h2 className="font-cormorant text-4xl md:text-5xl font-semibold text-oceano mb-4" data-id="element-82" data-mp="3rxt1h18iqzjv">
              ¿Qué incluye el método?
            </h2>
          </div>

          <div className="flex flex-col gap-8 md:grid md:grid-cols-2 lg:grid-cols-3" data-id="element-83" data-mp="2ginu3jxg0ctx">
            {/* Card 1 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-84" data-mp="inqfxhqyd1nf">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-85" data-mp="en6ied2gvw0r">
                <img src={IMAGES.aerofobia} alt="Ebook" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-86" data-mp="sebamu3i9m12" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-87" data-mp="12awkbsqgzlb8">
                <div className="flex items-center gap-2 mb-3" data-id="element-88" data-mp="12cr4h4tx39e9">
                  <BookOpen size={18} className="text-caribe" data-id="element-89" data-mp="xb1rhjn4wlnj" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-90" data-mp="1bc4f40ts240k">
                    Ebook principal
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-91" data-mp="16aow0eb5u7kt">
                  Aerofobia: Guía para Volar en Calma
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-92" data-mp="385fo34d80i3q">
                  La guía base para entender el miedo, conocer qué pasa en el
                  avión y aprender herramientas para viajar con más
                  tranquilidad.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-93" data-mp="1erpgja9qp53u">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-94" data-mp="14vm4ncp35s3p">
                <img src={IMAGES.guiaSos} alt="Guía SOS" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-95" data-mp="19ufk3anxnt6i" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-96" data-mp="vvpipiu2z7cc">
                <div className="flex items-center gap-2 mb-3" data-id="element-97" data-mp="zvhlfewsmj9s">
                  <Smartphone size={18} className="text-caribe" data-id="element-98" data-mp="zy2pmakkdbap" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-99" data-mp="2q9716rfmpe0i">
                    Recurso rápido
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-100" data-mp="7237xdosqm6g">
                  Guía SOS para leer en el avión
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-101" data-mp="2531v2ry5jfc">
                  Un recurso rápido para abrir desde el celular cuando aparece
                  ansiedad, turbulencia, miedo al despegue o sensación de
                  pérdida de control.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-102" data-mp="lqhd3v9oqmq6">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-103" data-mp="gyg2eds61429">
                <img src={IMAGES.biblioteca} alt="Biblioteca de Calma en Vuelo" className="max-w-full max-h-full object-contain drop-shadow-xl rounded-xl" data-id="element-104" data-mp="c04rdzq4wnig" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-105" data-mp="14pbpwypl9d3p">
                <div className="flex items-center gap-2 mb-3" data-id="element-106" data-mp="1c0ztnr4hoy3n">
                  <Headphones size={18} className="text-caribe" data-id="element-107" data-mp="1c2ko7gqt0h1i" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-108" data-mp="1h0vxnqd6spj7">
                    Audio
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-109" data-mp="1q0iyob207tbq">
                  Biblioteca de Calma en Vuelo
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-110" data-mp="1uz2ncj7zfj7s">
                  Incluye audios de soporte, meditaciones guiadas y podcasts
                  explicativos para acompañarte antes y durante el vuelo.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col md:col-start-1 lg:col-start-2" data-id="element-111" data-mp="29np3gn5fof02">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-112" data-mp="2ion9fazfmwla">
                <img src={IMAGES.cuaderno} alt="Cuaderno" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-113" data-mp="2527svm0sre56" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-114" data-mp="2102wwgmvm5fd">
                <div className="flex items-center gap-2 mb-3" data-id="element-115" data-mp="2er7rv5fau9jd">
                  <FileText size={18} className="text-caribe" data-id="element-116" data-mp="29t68ejdyo87d" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-117" data-mp="29ty6c0v16i0t">
                    Imprimible
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-118" data-mp="1i31t2z526bd0">
                  Cuaderno de ejercicios imprimible +30 páginas
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-119" data-mp="1r2y9kdhao3wz">
                  Ejercicios para ordenar tus pensamientos, identificar miedos,
                  preparar tu plan de calma y registrar tu avance.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="bg-white rounded-3xl overflow-hidden border border-travertino/20 shadow-sm flex flex-col" data-id="element-120" data-mp="oac9cygoaztv">
              <div className="p-6 bg-polar flex justify-center items-center h-64 md:h-72" data-id="element-121" data-mp="t9fgcog7rg4h">
                <img src={IMAGES.checklist} alt="Checklist" className="max-w-full max-h-full object-contain drop-shadow-xl" data-id="element-122" data-mp="f9xqs6v3fyu2" />
              </div>
              <div className="p-6 md:p-8 flex-1 border-t border-travertino/10" data-id="element-123" data-mp="k8r15qf339q0">
                <div className="flex items-center gap-2 mb-3" data-id="element-124" data-mp="17zcxwljn17kp">
                  <ListChecks size={18} className="text-caribe" data-id="element-125" data-mp="1c06m7g0zsoe4" />
                  <span className="text-xs font-bold text-caribe uppercase tracking-wider" data-id="element-126" data-mp="1c1rkngqfgrh9">
                    Organización
                  </span>
                </div>
                <h3 className="font-cormorant text-2xl font-semibold text-oceano mb-2" data-id="element-127" data-mp="135s9nl7ujryp">
                  Checklist pre-vuelo antiansiedad
                </h3>
                <p className="text-hierro text-sm leading-relaxed" data-id="element-128" data-mp="1vo5y4u7am1l4">
                  Una lista práctica para preparar documentos, objetos de calma,
                  audios, respiraciones y todo lo necesario antes de subir al
                  avión.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECCIÓN “CUÁNDO USARLO” */}
      <section className="py-16 md:py-20 px-4 bg-polar" data-id="element-129" data-mp="1qmm4aio9m2qf">
        <div className="max-w-4xl mx-auto" data-id="element-130" data-mp="1qm3zgxnjeg6w">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano text-center mb-12" data-id="element-131" data-mp="1mkihwz025dqd">
            Usalo justo cuando más lo necesitás
          </h2>

          <div className="space-y-6 relative before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-travertino/30" data-id="element-132" data-mp="1hrz5i5d3won5">
            {[{
            title: 'Antes del viaje',
            desc: 'Para ansiedad anticipatoria, pensamientos repetitivos y miedo antes de dormir.'
          }, {
            title: 'En el aeropuerto',
            desc: 'Para bajar nervios, ordenar la mente y no entrar al avión en alerta total.'
          }, {
            title: 'Durante el despegue',
            desc: 'Para respirar, entender los ruidos y atravesar los primeros minutos.'
          }, {
            title: 'En turbulencia',
            desc: 'Para recordar qué hacer, escuchar audios y evitar entrar en pánico.'
          }, {
            title: 'En pleno vuelo',
            desc: 'Para volver al presente cuando aparece sensación de encierro o pérdida de control.'
          }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-133" data-mp="2vueurjg2dbxi" data-mpi={i}>
                <div className="flex items-center justify-center w-12 h-12 rounded-full border-4 border-polar bg-caribe text-polar shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10" data-id="element-134" data-mp="35jgbxcychzd2">
                  <Check size={20} data-id="element-135" data-mp="2d2dr83scgxhr" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-3rem)] bg-white p-5 rounded-2xl shadow-sm border border-travertino/20" data-id="element-136" data-mp="2d0svwby7haiu">
                  <h3 className="font-cormorant text-xl font-semibold text-oceano mb-1" data-id="element-137" data-mp="11hi86yfijdy0">
                    {item.title}
                  </h3>
                  <p className="text-hierro text-sm leading-relaxed" data-id="element-138" data-mp="1fht1s987j6mv">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="py-16 md:py-20 px-4 bg-oceano text-polar" data-id="element-139" data-mp="1kfuiini5utv6">
        <div className="max-w-4xl mx-auto" data-id="element-140" data-mp="1kdi4glfix3ne">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-center mb-10" data-id="element-141" data-mp="1y7iwyxfvtufb">
            Con este método vas a poder…
          </h2>

          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-12" data-id="element-142" data-mp="1u7igz8d4xb65">
            {['Llegar al vuelo con un plan, no solo con miedo.', 'Entender sensaciones que antes te asustaban.', 'Tener una guía para leer en el avión.', 'Escuchar audios cuando necesitás calma.', 'Prepararte emocionalmente antes de viajar.', 'Sentirte más acompañada/o durante el vuelo.'].map((benefit, i) => <div key={i} className="flex items-start gap-3 bg-polar/5 p-4 rounded-xl border border-polar/10" data-id="element-143" data-mp="2rpnqj4aps3i3" data-mpi={i}>
                <CheckCircle2 className="text-caribe shrink-0 mt-0.5" size={20} data-id="element-144" data-mp="2ct6eeaupakuh" />
                <span className="text-polar/90 font-medium" data-id="element-145" data-mp="2hrrdu824p0hv">{benefit}</span>
              </div>)}
          </div>

          <div className="text-center" data-id="element-146" data-mp="23ztegoy7yiyw">
            <p className="font-cormorant text-2xl md:text-3xl text-travertino italic" data-id="element-147" data-mp="28s4d7oh83ypv">
              "No prometemos magia. Te damos algo más útil: herramientas
              concretas para atravesar el miedo."
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0] overflow-hidden" data-id="element-148" data-mp="1yzyr2vdnkbil">
        <div className="max-w-6xl mx-auto" data-id="element-149" data-mp="1l3bni9r72n3p">
          <div className="text-center mb-12" data-id="element-150" data-mp="1p4n5pu7qo61v">
            <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano mb-4" data-id="element-151" data-mp="1p3c33xq154cc">
              Personas que empezaron a viajar con más calma
            </h2>
          </div>

          <TestimonialCarousel data-id="element-152" data-mp="kbsebxc0zhi5" />
        </div>
      </section>

      {/* SECCIÓN DE COMPRA */}
      <section id="compra" className="py-16 md:py-24 px-4 bg-oceano relative" data-id="element-153" data-mp="6brac90grtyg">
        <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-154" data-mp="ab9o8ktvb4rs"></div>

        <div className="max-w-5xl mx-auto relative z-10" data-id="element-155" data-mp="y25hznhvwa1o">
          <div className="text-center mb-10" data-id="element-156" data-mp="1318icumx5s3b">
            <h2 className="font-cormorant text-4xl md:text-5xl lg:text-6xl font-semibold text-polar mb-4" data-id="element-157" data-mp="12zx9igem9u5c">
              Accedé hoy al Método Volar en Calma
            </h2>
          </div>

          <div className="bg-polar rounded-3xl p-6 md:p-12 shadow-2xl max-w-3xl mx-auto" data-id="element-158" data-mp="t9cgr9lmjjuc">
            {/* 1. Todo lo que incluye (primero y llamativo) */}
            <div className="mb-10" data-id="element-159" data-mp="1lrq6sn0py1iv">
              <h3 className="font-cormorant text-2xl md:text-3xl font-semibold text-oceano mb-6 text-center" data-id="element-160" data-mp="1lsrhurhqio0l">
                Todo lo que incluye el método
              </h3>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3" data-id="element-161" data-mp="31jafwp2uf0jj">
                {['Ebook principal Aerofobia: Guía para Volar en Calma', 'Guía SOS para leer en el avión', 'Audios de soporte para momentos de ansiedad', 'Meditaciones guiadas para viajar con calma', 'Podcasts explicativos para entender el miedo a volar', 'Cuaderno de ejercicios imprimible +30 páginas', 'Checklist pre-vuelo antiansiedad'].map((item, i) => <li key={i} className="flex items-start gap-3 text-oceano font-medium text-sm md:text-base" data-id="element-162" data-mp="2ikpsb705udan" data-mpi={i}>
                    <CheckCircle2 size={20} className="text-caribe shrink-0 mt-0.5" data-id="element-163" data-mp="2xii9s4p9pbrh" />
                    <span data-id="element-164" data-mp="2sgsz4q5yj03x">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* 2. Oferta especial por tiempo limitado y compra */}
            <div className="flex flex-col items-center text-center bg-white p-6 md:p-10 rounded-3xl border-2 border-travertino/20 shadow-sm" data-id="element-165" data-mp="7rvcrf3k0vxa">
              <div className="bg-red-50 text-red-600 px-4 py-2 rounded-full text-xs md:text-sm font-bold mb-6 flex items-center gap-2 animate-pulse" data-id="element-166" data-mp="eu0bxc3jnjcu">
                <Clock size={16} data-id="element-167" data-mp="ot8z9m3wlzq8" />
                Oferta especial por tiempo limitado
              </div>

              {isPriceLoading ? <div className="mb-6 flex flex-col items-center gap-3" aria-live="polite" aria-busy="true" data-id="element-168" data-mp="tsc7fff6km2f">
                  <div className="h-6 w-48 rounded-full bg-travertino/20 animate-pulse" data-id="element-169" data-mp="3sar6brbur2pl"></div>
                  <div className="h-12 md:h-14 w-56 rounded-xl bg-travertino/20 animate-pulse" data-id="element-170" data-mp="14gd2hd3368t"></div>
                  <span className="sr-only" data-id="element-171" data-mp="61gh6mqdhyxa">Calculando el precio para tu país…</span>
                </div> : <>
                  <div className="mb-1" data-id="element-172" data-mp="atzste5i2jzg">
                    <span className="text-hierro line-through text-lg md:text-xl" data-id="element-173" data-mp="39j859q1e1yq7">
                      Precio anterior: {price.originalPrice}
                    </span>
                  </div>
                  <div className="mb-6" data-id="element-174" data-mp="3efykkb5dcqak">
                    <span className="text-caribe font-bold text-4xl md:text-6xl" data-id="element-175" data-mp="1vnght1ztwaax">
                      {price.price}
                    </span>
                  </div>
                </>}

              <div className="w-full max-w-md space-y-4" data-id="element-176" data-mp="1mlz57tlmqqqz">
                <button onClick={buyNow} className="w-full bg-caribe hover:bg-[#24a8a2] text-polar py-4 md:py-5 rounded-xl font-bold text-lg md:text-xl transition-colors shadow-xl shadow-caribe/20" data-id="element-177" data-mp="1moldjvgchaa9">
                  QUIERO ACCEDER
                </button>

                <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-[11px] md:text-xs text-hierro font-medium pt-2" data-id="element-178" data-mp="17wjhynlb53g1">
                  <span className="flex items-center gap-1" data-id="element-179" data-mp="13tmuaigwuh58">
                    <ShieldCheck size={14} data-id="element-180" data-mp="yy7hltun2rae" /> Pago seguro
                  </span>
                  <span className="flex items-center gap-1" data-id="element-181" data-mp="ty32a75gw2lr">
                    <Download size={14} data-id="element-182" data-mp="oyqffmelivj6" /> Acceso inmediato
                  </span>
                  <span className="flex items-center gap-1" data-id="element-183" data-mp="p0te0uttdwzi">
                    <Smartphone size={14} data-id="element-184" data-mp="2p2137m9xlynj" /> 100% digital
                  </span>
                </div>
              </div>

              <p className="text-xs md:text-sm text-hierro mt-6 pt-6 border-t border-travertino/20" data-id="element-185" data-mp="2tzb01mznhcle">
                Recibís el acceso al contenido digital para empezar a usarlo
                antes de tu próximo vuelo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 px-4 bg-[#F5F5F0]" data-id="element-186" data-mp="26ckxmkje3g09">
        <div className="max-w-3xl mx-auto" data-id="element-187" data-mp="2bacrvl7ibe7m">
          <h2 className="font-cormorant text-3xl md:text-5xl font-semibold text-oceano text-center mb-10" data-id="element-188" data-mp="2b78i88b9vq9k">
            Preguntas frecuentes
          </h2>

          <div className="space-y-3" data-id="element-189" data-mp="21c6sba96ak5x">
            {FAQS.map((faq, i) => <div key={i} className="bg-white rounded-2xl border border-travertino/20 overflow-hidden" data-id="element-190" data-mp="3qqlcrid5rkvm" data-mpi={i}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-5 py-4 text-left flex justify-between items-center focus:outline-none" data-id="element-191" data-mp="3vpy7epa9tw8r">
                  <span className="font-semibold text-oceano pr-4 text-base md:text-lg" data-id="element-192" data-mp="1jzqbzrqa2xeo">
                    {faq.q}
                  </span>
                  <ChevronRight className={`text-caribe shrink-0 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} size={20} data-id="element-193" data-mp="1ts5zt5o9ag7h" />
                </button>
                {openFaq === i && <div className="px-5 pb-4 text-hierro text-sm md:text-base leading-relaxed" data-id="element-194" data-mp="22sav4hg6do5a">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* CIERRE FINAL */}

      {/* Footer */}
      <footer className="bg-oceano text-polar/60 py-12 px-4 text-sm text-center border-t border-polar/10" data-id="element-195" data-mp="2cpyy1unxq1yj">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-196" data-mp="27pkno5yv2fxc">
          <div className="inline-block bg-polar rounded-2xl px-6 py-4 shadow-sm" data-id="element-197" data-mp="idsd4458o6t1">
            <img src={IMAGES.logo} alt="Atlas de Viajes" className="h-14 md:h-16 mx-auto object-contain" data-id="element-198" data-mp="de5wrlu46ag0" />
          </div>
          <p className="leading-relaxed" data-id="element-199" data-mp="2dgxccwplq3jj">
            Este producto es un recurso educativo y de acompañamiento. No
            reemplaza atención médica, psicológica ni tratamiento profesional.
            Si tu ansiedad es muy intensa, frecuente o limita tu vida diaria,
            recomendamos consultar con un profesional de salud mental.
          </p>
          <p className="pt-4 border-t border-polar/10" data-id="element-200" data-mp="2wernbgaaip4k">
            © {new Date().getFullYear()} Atlas de Viajes. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>
    </div>;
}